import React from "react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ReadOnlyField from "../create_training_content/FormFieldReadOnly";
import TraineeActionModal from "./TraineeActionModal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_post_learning_form_grade,
  API_post_learning_form_ai_grade_suggestion,
} from "../../../constants";

const isManualType = (t) => t === "short_text" || t === "long_text";

// Manager-facing grading view: shows a learner's submitted answers and lets the
// manager assign points to each manually-graded free-text field. Auto-scored
// fields (scale / single_select) are counted server-side at save.
function FormGradeModal({ open, onClose, record, submission, homeCompany, onGraded }) {
  // Grade against the field defs the submission was made with (snapshot), so the
  // max_points / answer key match what the learner actually answered.
  const fields =
    submission?.fields && submission.fields.length
      ? submission.fields
      : record?.fields || [];
  const responses = submission?.responses || {};
  const gradable = fields.filter(
    (f) => f.scored && isManualType(f.type)
  );

  // Show (and grade against) the pass threshold in effect when the learner
  // submitted — the backend snapshots it, so an admin editing the form later
  // doesn't change how this submission is judged.
  const submitCfg = submission?.scoring_snapshot;
  const effectiveThreshold =
    submitCfg && Object.prototype.hasOwnProperty.call(submitCfg, "pass_threshold")
      ? submitCfg.pass_threshold
      : record?.pass_threshold;

  const [scores, setScores] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [aiLoading, setAiLoading] = React.useState(false);
  // AI rationales by field id, shown next to each suggested score.
  const [rationales, setRationales] = React.useState({});

  React.useEffect(() => {
    if (!open) return;
    const init = {};
    gradable.forEach((f) => {
      const prior = submission?.field_scores?.[f.id];
      init[f.id] = prior === undefined || prior === null ? "" : String(prior);
    });
    setScores(init);
    // Clear any AI rationales from a previously-opened submission.
    setRationales({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submission]);

  const maxManual = gradable.reduce(
    (s, f) => s + (Number(f.max_points) || 0),
    0
  );
  const manualTotal = gradable.reduce(
    (s, f) => s + (Number(scores[f.id]) || 0),
    0
  );

  const fieldValid = (f) => {
    const v = scores[f.id];
    if (v === "" || v === undefined) return false;
    const n = Number(v);
    const cap = Number(f.max_points) || 0;
    return Number.isFinite(n) && n >= 0 && n <= cap;
  };
  const allValid = gradable.length > 0 && gradable.every(fieldValid);

  const handleAiSuggest = async () => {
    if (aiLoading || saving || gradable.length === 0) return;
    setAiLoading(true);
    try {
      const res = await postRightworkAPI(
        API_post_learning_form_ai_grade_suggestion,
        {
          company_id: homeCompany,
          learning_form_uuid: record.form_uuid,
          section_uuid: record.section_uuid,
          store_team_member_id: submission.store_team_member_id,
        }
      );
      if (res.status === 200) {
        const suggestions = res.data?.redux?.payload?.suggestions || {};
        // Prefill the point inputs with the suggestions; the manager can edit
        // any of them before saving. Never auto-saves.
        setScores((prev) => {
          const next = { ...prev };
          gradable.forEach((f) => {
            const s = suggestions[f.id];
            if (s && s.points !== undefined && s.points !== null) {
              next[f.id] = String(s.points);
            }
          });
          return next;
        });
        const rats = {};
        gradable.forEach((f) => {
          if (suggestions[f.id]?.rationale) rats[f.id] = suggestions[f.id].rationale;
        });
        setRationales(rats);
        toast.success("AI suggestions added, review before saving", {
          autoClose: 2500,
        });
      }
    } catch (error) {
      toast.error(
        error.response?.data?.system?.message ||
          "AI grading is unavailable right now",
        { autoClose: 3000 }
      );
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async () => {
    if (!allValid || saving) return;
    setSaving(true);
    try {
      const field_scores = {};
      gradable.forEach((f) => {
        field_scores[f.id] = Number(scores[f.id]) || 0;
      });
      const res = await postRightworkAPI(API_post_learning_form_grade, {
        company_id: homeCompany,
        learning_form_uuid: record.form_uuid,
        section_uuid: record.section_uuid,
        store_team_member_id: submission.store_team_member_id,
        field_scores: JSON.stringify(field_scores),
        // Concurrency tokens the manager reviewed. The backend rejects the save
        // if the learner resubmitted (expected_updated_at) OR another manager
        // graded it (expected_graded_at) since the modal opened.
        expected_updated_at: submission.date_completed,
        expected_graded_at: submission.graded_at ?? null,
      });
      if (res.status === 200) {
        const payload = res.data?.redux?.payload;
        const completion = payload?.form_completion;
        toast.success("Grade saved", { autoClose: 2000 });
        if (onGraded) onGraded(submission, completion, payload?.submission_graded_at);
        onClose();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.system?.message || "Failed to save grade",
        { autoClose: 3000 }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <TraineeActionModal
      open={open && !!submission}
      onClose={onClose}
      title={`Grade: ${submission?.employee_name || "Submission"}`}
      subtitle={record?.name || "Form"}
      primaryLabel="Save grade"
      onPrimary={handleSave}
      primaryDisabled={!allValid || aiLoading}
      primaryBusy={saving}
    >
      {submission && (
        <>
            {/* Running total */}
            <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
              <span className="rounded-full bg-violet-100 px-2.5 py-0.5 font-medium text-violet-700">
                Manual points {manualTotal} / {maxManual}
              </span>
              {effectiveThreshold !== null &&
                effectiveThreshold !== undefined && (
                  <span className="rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-600">
                    Pass threshold {effectiveThreshold}
                  </span>
                )}
              <span className="text-slate-400">
                Auto-scored fields are added on save.
              </span>
            </div>

            {/* AI assist */}
            {gradable.length > 0 && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleAiSuggest}
                  disabled={aiLoading || saving}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-violet-200 text-violet-700 bg-white hover:bg-violet-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {aiLoading ? (
                    "Grading…"
                  ) : (
                    <>
                      <AutoAwesomeIcon style={{ fontSize: 16 }} />
                      Grade with AI
                    </>
                  )}
                </button>
                <span className="text-xs text-slate-400">
                  Suggestions are drafts. Review and edit before saving.
                </span>
              </div>
            )}

            {fields.length === 0 ? (
              <p className="text-sm text-slate-400">No fields on this form.</p>
            ) : (
              <div className="space-y-4">
                {fields.map((f) => {
                  const isGradable = f.scored && isManualType(f.type);
                  return (
                    <div key={f.id}>
                      <ReadOnlyField field={f} value={responses[f.id]} answered showScoring />
                      {isGradable && (
                        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                          {f.answer_key && (
                            <div className="mb-2 text-xs text-slate-700">
                              <span className="font-semibold text-slate-800">
                                Answer key:
                              </span>{" "}
                              {f.answer_key}
                            </div>
                          )}
                          <label className="flex items-center gap-2 text-sm text-slate-700">
                            <span className="font-medium">Points</span>
                            <input
                              type="number"
                              min={0}
                              max={Number(f.max_points) || 0}
                              value={scores[f.id] ?? ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                setScores((prev) => ({ ...prev, [f.id]: val }));
                                // The AI rationale justified the suggested
                                // number; once the manager overrides it, drop
                                // the stale rationale so it can't mislead.
                                setRationales((prev) => {
                                  if (!(f.id in prev)) return prev;
                                  const next = { ...prev };
                                  delete next[f.id];
                                  return next;
                                });
                              }}
                              className={`w-20 px-2 py-1 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-600 ${
                                scores[f.id] !== "" && !fieldValid(f)
                                  ? "border-red-400"
                                  : "border-gray-300"
                              }`}
                            />
                            <span className="text-xs text-slate-500">
                              / {Number(f.max_points) || 0}
                            </span>
                          </label>
                          {rationales[f.id] && (
                            <p className="mt-1.5 text-xs text-violet-700">
                              <span className="font-semibold inline-flex items-center gap-1 align-text-bottom">
                                <AutoAwesomeIcon style={{ fontSize: 13 }} />
                                AI:
                              </span>{" "}
                              {rationales[f.id]}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
        </>
      )}
    </TraineeActionModal>
  );
}

export default FormGradeModal;
